import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useServerFn } from "@tanstack/react-start";
import { getPaymentStatusPublic } from "@/lib/payos.functions";

type Search = { order?: string };

export const Route = createFileRoute("/payment-success")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    order: typeof s.order === "string" ? s.order : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Thanh toán thành công — Ezyclass" },
      { name: "description", content: "Xác nhận thanh toán gói Ezyclass thành công. Tài khoản của bạn đã được kích hoạt và sẵn sàng sử dụng." },
      { property: "og:title", content: "Thanh toán thành công — Ezyclass" },
      { property: "og:description", content: "Xác nhận đơn hàng Ezyclass đã thanh toán thành công và tài khoản đã được kích hoạt." },
      { property: "og:url", content: "https://ezyclass.com/payment-success" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://ezyclass.com/payment-success" }],
  }),
  component: PaymentSuccess,
});


const PLAN_LABEL: Record<string, string> = {
  tutor: "Gia sư",
  center: "Trung tâm",
  school: "Trường học",
};

function PaymentSuccess() {
  const { order } = Route.useSearch();
  const navigate = useNavigate();
  const { user } = useAuth();

  const checkStatusFn = useServerFn(getPaymentStatusPublic);

  const paymentQ = useQuery({
    queryKey: ["payment-status", order],
    enabled: !!order,
    refetchInterval: (q) => (q.state.data?.isPaid ? false : 2500),
    queryFn: async () => await checkStatusFn({ data: { orderCode: Number(order) } }),
  });

  const isPaid = paymentQ.data?.isPaid === true;

  React.useEffect(() => {
    if (!isPaid) return;
    const t = setTimeout(() => {
      if (user && !user.onboarded) navigate({ to: "/onboarding" });
      else navigate({ to: "/admin" });
    }, 3000);
    return () => clearTimeout(t);
  }, [isPaid, user, navigate]);

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 20 }}
        className="text-center max-w-md"
      >
        {isPaid ? (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2, stiffness: 300, damping: 15 }}
              className="size-20 rounded-3xl bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-100 dark:ring-emerald-500/20 grid place-items-center mx-auto mb-6"
            >
              <CheckCircle2 className="size-10 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
            </motion.div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
              Thanh toán thành công!
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-3">
              Gói <strong className="text-slate-700 dark:text-slate-200">{PLAN_LABEL[paymentQ.data?.plan ?? ""] ?? paymentQ.data?.plan}</strong> đã được kích hoạt. Hãy hoàn tất thiết lập.
            </p>
            <Button
              asChild
              className="mt-8 h-11 px-8 rounded-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 font-medium active:scale-[0.98] transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]"
            >
              <Link to={user && !user.onboarded ? "/onboarding" : "/admin"}>
                Tiếp tục thiết lập <ArrowRight className="size-4 ml-1.5" strokeWidth={1.5} />
              </Link>
            </Button>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-4">Tự động chuyển sau 3 giây...</p>
          </>
        ) : (
          <>
            <div className="size-20 rounded-3xl bg-blue-50 dark:bg-blue-500/10 ring-1 ring-blue-100 dark:ring-blue-500/20 grid place-items-center mx-auto mb-6">
              <Loader2 className="size-10 text-blue-600 dark:text-blue-400 animate-spin" strokeWidth={1.5} />
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              Đang xác nhận thanh toán...
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-3 leading-relaxed">
              Vui lòng đợi trong giây lát. Hệ thống sẽ tự cập nhật khi nhận được xác nhận từ ngân hàng.
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
